import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useParams, useNavigate } from 'react-router-dom';
import { fetchGalleryByEvents } from '@/services/api';
import ImageModal from '@/components/ImageModal';
import SeoHead from '@/components/Seo';
import { ChevronLeft, Maximize2, Calendar, Share2, Info } from 'lucide-react';

interface ImageItem {
    id: number;
    source: string;
    caption: string | null;
    title: string;
    created_at: string;
}

const AlbumPage = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();

    const [images, setImages] = useState<ImageItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [title, setTitle] = useState('');
    const [date, setDate] = useState('');
    const [selectedImage, setSelectedImage] = useState<string | null>(null);
    const [ogImage, setOgImage] = useState<string | null>(null);

    useEffect(() => {
        if (!id) return;

        const fetchAlbum = async () => {
            try {
                const response = await fetchGalleryByEvents(id!);
                const data = response.data;
                setImages(data.images || []);
                setTitle(data.title || 'Gallery');
                setDate(data.created_at || '');

                if (data.images && data.images.length > 0) {
                    setOgImage(data.images[0].source);
                }
            } catch (err) {
                console.error('Failed to fetch album', err);
            } finally {
                setLoading(false);
            }
        };

        fetchAlbum();
    }, [id]);

    const handleShare = () => {
        if (navigator.share) {
            navigator.share({
                title: title,
                url: window.location.href,
            });
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-white">
                <div className="flex flex-col items-center gap-6">
                    <div className="relative w-16 h-16">
                        <div className="absolute inset-0 border-4 border-amber-100 rounded-full" />
                        <div className="absolute inset-0 border-4 border-amber-600 border-t-transparent rounded-full animate-spin" />
                    </div>
                    <p className="text-slate-400 font-serif italic animate-pulse">Opening the Archives...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-[#fcfaf7] min-h-screen pb-32">
            <SeoHead
                title={`${title} | Diocesan Gallery`}
                description={`${title} — Photo highlights and visual records from the Diocese of Ibadan South.`}
                ogImage={ogImage}
            />

            {/* Sticky Navigation */}
            <nav className="sticky top-0 z-40 bg-white/70 backdrop-blur-xl border-b border-slate-200/50 px-6 py-4">
                <div className="max-w-7xl mx-auto flex items-center justify-between">
                    <button
                        onClick={() => navigate('/gallery')}
                        className="group flex items-center gap-3 text-slate-900 transition-all"
                    >
                        <div className="p-2 rounded-full group-hover:bg-amber-50 transition-colors">
                            <ChevronLeft size={18} className="group-hover:-translate-x-1 transition-transform" />
                        </div>
                        <span className="text-[10px] font-black uppercase tracking-[0.2em]">Archives</span>
                    </button>

                    <button
                        onClick={handleShare}
                        className="p-2.5 rounded-full hover:bg-slate-100 text-slate-500 hover:text-slate-900 transition-all"
                    >
                        <Share2 size={18} />
                    </button>
                </div>
            </nav>

            {/* Hero Header */}
            <header className="max-w-5xl mx-auto px-6 pt-24 pb-20 text-center">
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                >
                    <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-50 text-amber-700 rounded-full mb-8">
                        <Calendar size={14} />
                        <span className="text-[10px] font-black uppercase tracking-widest">
                            {date ? new Date(date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : 'Diocesan Record'}
                        </span>
                    </div>

                    <h1 className="text-5xl md:text-7xl font-serif font-bold text-slate-900 leading-[1.1] mb-8">
                        {title}
                    </h1>

                    <div className="flex justify-center items-center gap-4 text-slate-400">
                        <div className="h-px w-12 bg-slate-200" />
                        <span className="text-xs font-serif italic">{images.length} Photographs</span>
                        <div className="h-px w-12 bg-slate-200" />
                    </div>
                </motion.div>
            </header>

            {/* Gallery Grid */}
            <main className="max-w-7xl mx-auto px-6">
                {images.length === 0 ? (
                    <div className="py-32 text-center bg-white rounded-[4rem] border border-slate-100 shadow-sm">
                        <Info className="w-12 h-12 text-slate-200 mx-auto mb-4" />
                        <p className="text-slate-400 font-serif italic text-lg">This collection is currently being digitized.</p>
                    </div>
                ) : (
                    <div className="columns-1 sm:columns-2 lg:columns-3 gap-8 space-y-8">
                        {images.map((img, index) => (
                            <motion.div
                                key={img.id}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-50px" }}
                                transition={{ delay: (index % 6) * 0.1 }}
                                className="relative group cursor-zoom-in break-inside-avoid"
                                onClick={() => setSelectedImage(img.source)}
                            >
                                <div className="relative overflow-hidden rounded-[2rem] bg-white shadow-xl shadow-slate-200/60 ring-1 ring-slate-100 transition-all duration-500 group-hover:shadow-2xl group-hover:shadow-amber-900/10 group-hover:-translate-y-1">
                                    <img
                                        src={img.source}
                                        alt={img.caption || img.title}
                                        className="w-full h-auto object-cover transition-transform duration-[2s] group-hover:scale-110"
                                        loading="lazy"
                                    />

                                    {/* Hover Action */}
                                    <div className="absolute inset-0 bg-slate-900/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex items-center justify-center">
                                        <div className="w-14 h-14 bg-white/20 backdrop-blur-xl rounded-full flex items-center justify-center text-white scale-90 group-hover:scale-100 transition-transform duration-500">
                                            <Maximize2 size={24} />
                                        </div>
                                    </div>
                                </div>

                                {img.caption && (
                                    <div className="mt-4 px-4">
                                        <p className="text-xs font-serif italic text-slate-500 leading-relaxed text-center">
                                            {img.caption}
                                        </p>
                                    </div>
                                )}
                            </motion.div>
                        ))}
                    </div>
                )}
            </main>

            <AnimatePresence>
                {selectedImage && (
                    <ImageModal
                        imageUrl={selectedImage}
                        onClose={() => setSelectedImage(null)}
                    />
                )}
            </AnimatePresence>
        </div>
    );
};

export default AlbumPage;